import { Component } from '@theme/component';

const DEFAULT_SORT = 'date-desc';
const INITIAL_VISIBLE_COUNT = 9;
const MOBILE_INITIAL_VISIBLE_COUNT = 6;
const LOAD_MORE_COUNT = 6;
const MOBILE_MEDIA_QUERY = '(max-width: 749px)';
const FILTER_QUERY_PARAM = 'filter_tags';
const DEFAULT_STOREFRONT_DOMAIN = 'give-a-shit-eco-products.myshopify.com';
const DEFAULT_STOREFRONT_API_VERSION = '2026-04';
const SORT_VALUES = new Set(['date-desc', 'date-asc', 'title-asc', 'title-desc']);
const SORT_CONFIG = {
  'date-desc': { sortKey: 'PUBLISHED_AT', reverse: true },
  'date-asc': { sortKey: 'PUBLISHED_AT', reverse: false },
  'title-asc': { sortKey: 'TITLE', reverse: false },
  'title-desc': { sortKey: 'TITLE', reverse: true },
};
const BLOG_ARTICLES_QUERY = `
  query BlogArticles(
    $handle: String!
    $first: Int!
    $after: String
    $sortKey: ArticleSortKeys!
    $reverse: Boolean!
    $query: String
  ) {
    blog(handle: $handle) {
      articles(
        first: $first
        after: $after
        sortKey: $sortKey
        reverse: $reverse
        query: $query
      ) {
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            handle
            title
            excerpt(truncateAt: 180)
            publishedAt
            tags
            image {
              altText
              width
              height
              src: url(transform: { maxWidth: 960 })
            }
          }
        }
      }
    }
  }
`;

/**
 * A custom element that renders a paginated blog posts list from the Storefront API.
 */
export default class BlogPostsList extends Component {
  #isLoading = false;
  #activeFilterState = [];
  #activeSortValue = DEFAULT_SORT;
  #hasNextPage = false;
  #endCursor = null;
  #requestCache = new Map();
  #mobileMediaQueryList = null;
  #responsiveInitialVisibleCount = INITIAL_VISIBLE_COUNT;
  #pendingResponsiveSync = false;

  connectedCallback() {
    super.connectedCallback();

    this.#mobileMediaQueryList = window.matchMedia(MOBILE_MEDIA_QUERY);
    this.#responsiveInitialVisibleCount = this.#getInitialVisibleCount();
    this.#addResponsiveListener();
    this.#renderFilterGroupsFromSources();
    window.addEventListener('popstate', this.#handlePopState);

    void this.#initialize();
  }

  disconnectedCallback() {
    this.#removeResponsiveListener();
    window.removeEventListener('popstate', this.#handlePopState);
    super.disconnectedCallback();
  }

  /**
   * Handles sort changes from the blog toolbar.
   * @param {Event} event
   */
  handleSortChange(event) {
    const target = event.composedPath?.()[0] ?? event.target;

    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
    if (!SORT_VALUES.has(target.value)) return;
    if (this.#isLoading) return;

    this.#closeSortPanels();
    if (target.value === this.#activeSortValue) return;

    void this.#applyState(this.#activeFilterState, target.value, { updateUrl: true });
  }

  handleViewMore() {
    if (this.#isLoading || !this.#hasNextPage) return;
    void this.#loadMore();
  }

  /**
   * Applies the selected blog tag filters using the Storefront API.
   * Tags that are active but not exposed in the current curated UI are preserved until explicitly removed.
   *
   * @param {Event} event
   */
  handleApplyFilters(event) {
    event.preventDefault();

    if (this.#isLoading) return;

    const selectedFilters = this.#getSelectedConfiguredFilters();
    const configuredHandles = new Set(this.#getConfiguredFilters().map((filter) => filter.handle));
    const preservedHiddenFilters = this.#activeFilterState.filter((filter) => !configuredHandles.has(filter.handle));
    const nextFilters = this.#dedupeFilterState([...selectedFilters, ...preservedHiddenFilters]);

    if (this.#areSameFilters(nextFilters, this.#activeFilterState)) {
      void this.#closeFiltersDrawer();
      return;
    }

    void this.#applyState(nextFilters, this.#activeSortValue, { updateUrl: true, closeDrawer: true });
  }

  async #initialize() {
    const initialFilters = this.#getInitialFilterState();
    const initialSort = this.#getSortValueFromUrl();

    this.#activeFilterState = this.#dedupeFilterState(initialFilters);
    this.#activeSortValue = initialSort;
    this.#syncFilterInputs();
    this.#renderActiveFilters();
    this.#updateSortControls(initialSort);
    this.#normalizeFilterUrl();
    this.#updateResultsUi();

    try {
      this.#setLoadingState(true);
      const pageData = await this.#fetchArticles({
        first: this.#getInitialVisibleCount(),
        filters: initialFilters,
        sortValue: initialSort,
      });

      if (!this.isConnected) return;

      this.#responsiveInitialVisibleCount = this.#getInitialVisibleCount();
      this.#commitResults(pageData, { append: false });
    } catch (error) {
      console.error(error);
    } finally {
      this.#setLoadingState(false);
    }
  }

  /**
   * @param {{ value: string, handle: string, label: string, group: string }[]} nextFilters
   * @param {string} sortValue
   * @param {{ updateUrl?: boolean, closeDrawer?: boolean, historyMode?: 'push' | 'replace' }} [options]
   */
  async #applyState(nextFilters, sortValue, { updateUrl = false, closeDrawer = false, historyMode = 'push' } = {}) {
    const normalizedFilters = this.#dedupeFilterState(nextFilters);
    const normalizedSort = SORT_VALUES.has(sortValue) ? sortValue : DEFAULT_SORT;

    try {
      this.#setLoadingState(true);

      const pageData = await this.#fetchArticles({
        first: this.#getInitialVisibleCount(),
        filters: normalizedFilters,
        sortValue: normalizedSort,
      });

      if (!this.isConnected) return;

      this.#activeFilterState = normalizedFilters;
      this.#activeSortValue = normalizedSort;
      this.#syncFilterInputs();
      this.#renderActiveFilters();
      this.#updateSortControls(normalizedSort);
      this.#responsiveInitialVisibleCount = this.#getInitialVisibleCount();
      this.#commitResults(pageData, { append: false });

      if (updateUrl) {
        const nextUrl = this.#buildFilterUrl(this.#activeFilterState, { sortValue: normalizedSort });
        history[historyMode === 'replace' ? 'replaceState' : 'pushState'](history.state, '', nextUrl);
      }

      this.#normalizeFilterUrl();

      if (closeDrawer) {
        await this.#closeFiltersDrawer();
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.#setLoadingState(false);
    }
  }

  async #loadMore() {
    if (!this.#endCursor) return;

    try {
      this.#setLoadingState(true);

      const pageData = await this.#fetchArticles({
        first: this.#getLoadMoreCount(),
        after: this.#endCursor,
        filters: this.#activeFilterState,
        sortValue: this.#activeSortValue,
      });

      if (!this.isConnected) return;

      this.#commitResults(pageData, { append: true });
    } catch (error) {
      console.error(error);
    } finally {
      this.#setLoadingState(false);
    }
  }

  /**
   * @param {{ first: number, after?: string | null, filters?: { value: string, handle: string, label: string, group: string }[], sortValue?: string }} options
   * @returns {Promise<{ articles: object[], pageInfo: { hasNextPage: boolean, endCursor: string | null } }>}
   */
  async #fetchArticles({ first, after = null, filters = this.#activeFilterState, sortValue = this.#activeSortValue }) {
    const { sortKey, reverse } = SORT_CONFIG[SORT_VALUES.has(sortValue) ? sortValue : DEFAULT_SORT];
    const filterQuery = this.#buildStorefrontFilterQuery(filters);
    const variables = {
      handle: this.blogHandle,
      first,
      after,
      sortKey,
      reverse,
      query: filterQuery || null,
    };
    const cacheKey = JSON.stringify(variables);
    const cachedResponse = this.#requestCache.get(cacheKey);

    if (cachedResponse) {
      return cachedResponse;
    }

    const response = await fetch(this.storefrontEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: BLOG_ARTICLES_QUERY,
        variables,
      }),
    });

    if (!response.ok) {
      throw new Error(`Storefront API request failed with status ${response.status}`);
    }

    const payload = await response.json();

    if (Array.isArray(payload.errors) && payload.errors.length > 0) {
      throw new Error(payload.errors.map((error) => error.message).join('\n'));
    }

    const connection = payload?.data?.blog?.articles;

    if (!connection) {
      throw new Error(`Blog "${this.blogHandle}" was not returned by the Storefront API`);
    }

    const pageData = {
      articles: Array.isArray(connection.edges)
        ? connection.edges
          .map((edge) => edge?.node)
          .filter(Boolean)
        : [],
      pageInfo: {
        hasNextPage: Boolean(connection.pageInfo?.hasNextPage),
        endCursor: connection.pageInfo?.endCursor ?? null,
      },
    };

    this.#requestCache.set(cacheKey, pageData);
    return pageData;
  }

  /**
   * @param {{ articles: object[], pageInfo: { hasNextPage: boolean, endCursor: string | null } }} pageData
   * @param {{ append?: boolean }} [options]
   */
  #commitResults(pageData, { append = false } = {}) {
    const grid = this.#getGrid();
    if (!(grid instanceof HTMLElement)) return;

    const articleCards = this.#renderArticleCards(pageData.articles);

    if (append) {
      grid.append(...articleCards);
    } else {
      grid.replaceChildren(...articleCards);
    }

    this.#hasNextPage = Boolean(pageData.pageInfo?.hasNextPage);
    this.#endCursor = pageData.pageInfo?.endCursor ?? null;
    this.#updateResultsUi();
  }

  /**
   * @param {object[]} articles
   * @returns {HTMLElement[]}
   */
  #renderArticleCards(articles) {
    return articles
      .map((article, index) => this.#createArticleCard(article, index))
      .filter((card) => card instanceof HTMLElement);
  }

  /**
   * @param {object} article
   * @param {number} index
   * @returns {HTMLElement | null}
   */
  #createArticleCard(article, index) {
    if (!article?.handle || !article?.title) return null;

    const template = this.#getCardTemplate();
    if (!(template instanceof HTMLTemplateElement)) return null;

    const card = template.content.firstElementChild?.cloneNode(true);
    if (!(card instanceof HTMLElement)) return null;

    const articleUrl = `${this.blogUrl.replace(/\/$/, '')}/${article.handle}`;
    const articleTags = Array.isArray(article.tags) ? article.tags : [];
    const excerptSource = article.excerpt || '';
    const excerptText = truncateWords(stripHtml(excerptSource), 20);
    const publishedAt = article.publishedAt || '';
    const publishedTimestamp = publishedAt ? Math.floor(new Date(publishedAt).getTime() / 1000) : 0;
    const tagHandles = articleTags.map((tag) => handleize(tag)).join('|');
    const formattedDate = formatDate(publishedAt, this.locale);

    card.dataset.blogIndex = `${this.sectionId}-api-${index}`;
    card.dataset.publishedAt = String(publishedTimestamp);
    card.dataset.title = article.title;
    card.dataset.tagHandles = tagHandles;
    card.dataset.initialIndex = String(index);
    card.dataset.page = '1';

    card.querySelectorAll('[data-blog-card-link]').forEach((link) => {
      if (link instanceof HTMLAnchorElement) {
        link.href = articleUrl;
      }
    });

    const titleElement = card.querySelector('[data-blog-card-title]');
    if (titleElement instanceof HTMLElement) {
      titleElement.textContent = article.title;
    }

    const imageLink = card.querySelector('[data-blog-card-image-link]');
    const imageElement = card.querySelector('[data-blog-card-image]');
    const image = article.image;

    if (imageLink instanceof HTMLElement) {
      const hasImage = Boolean(image?.src && imageElement instanceof HTMLImageElement);
      imageLink.hidden = !hasImage;

      if (hasImage && imageElement instanceof HTMLImageElement) {
        if (imageLink instanceof HTMLAnchorElement) {
          imageLink.href = articleUrl;
          imageLink.setAttribute('aria-label', article.title);
        }

        imageElement.src = image.src;
        imageElement.alt = image.altText || article.title;

        if (image.width) {
          imageElement.width = image.width;
        } else {
          imageElement.removeAttribute('width');
        }

        if (image.height) {
          imageElement.height = image.height;
        } else {
          imageElement.removeAttribute('height');
        }
      }
    }

    const tagsElement = card.querySelector('[data-blog-card-tags]');
    if (tagsElement instanceof HTMLElement) {
      tagsElement.replaceChildren();

      const visibleTags = articleTags.slice(0, 2);
      tagsElement.hidden = visibleTags.length === 0;

      visibleTags.forEach((tag) => {
        const tagElement = document.createElement('li');
        tagElement.className = 'blog-post-card__tag';
        tagElement.textContent = `#${tag}`;
        tagsElement.append(tagElement);
      });
    }

    const excerptElement = card.querySelector('[data-blog-card-excerpt]');
    if (excerptElement instanceof HTMLElement) {
      excerptElement.textContent = excerptText;
      excerptElement.hidden = excerptText === '';
    }

    const dateWrapper = card.querySelector('[data-blog-card-date-wrapper]');
    const dateElement = card.querySelector('[data-blog-card-date]');
    const hasDate = Boolean(publishedAt && formattedDate && dateElement instanceof HTMLTimeElement);

    if (dateWrapper instanceof HTMLElement) {
      dateWrapper.hidden = !hasDate;
    }

    if (hasDate && dateElement instanceof HTMLTimeElement) {
      dateElement.dateTime = publishedAt;
      dateElement.textContent = formattedDate;
    }

    return card;
  }

  #updateResultsUi() {
    const grid = this.#getGrid();
    const hasResults = Boolean(grid?.children.length);
    const viewMoreContainer = this.querySelector('.blog-posts-view-more');
    const viewMoreButton = this.querySelector('[data-blog-view-more-button]');
    const emptyState = this.#getEmptyState();

    if (grid instanceof HTMLElement) {
      grid.setAttribute('aria-busy', String(this.#isLoading));
    }

    if (viewMoreContainer instanceof HTMLElement) {
      viewMoreContainer.hidden = !this.#hasNextPage;
    }

    if (viewMoreButton instanceof HTMLButtonElement) {
      viewMoreButton.hidden = !this.#hasNextPage;
      viewMoreButton.disabled = this.#isLoading || !this.#hasNextPage;
      viewMoreButton.setAttribute('aria-busy', String(this.#isLoading));
    }

    if (emptyState instanceof HTMLElement) {
      emptyState.hidden = this.#isLoading || hasResults;
    }
  }

  /**
   * @param {boolean} isLoading
   */
  #setLoadingState(isLoading) {
    this.#isLoading = isLoading;
    this.#updateResultsUi();

    if (!isLoading && this.#pendingResponsiveSync) {
      this.#pendingResponsiveSync = false;
      void this.#syncResponsiveInitialCount();
    }
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getGrid() {
    const { grid } = this.refs;
    return grid instanceof HTMLElement ? grid : null;
  }

  /**
   * @returns {number}
   */
  #getInitialVisibleCount() {
    const count = Number(this.dataset.initialVisibleCount ?? INITIAL_VISIBLE_COUNT);
    const normalizedCount = Number.isFinite(count) && count > 0 ? Math.floor(count) : INITIAL_VISIBLE_COUNT;

    if (window.matchMedia(MOBILE_MEDIA_QUERY).matches) {
      return Math.min(normalizedCount, MOBILE_INITIAL_VISIBLE_COUNT);
    }

    return normalizedCount;
  }

  /**
   * @returns {number}
   */
  #getLoadMoreCount() {
    const count = Number(this.dataset.loadMoreCount ?? LOAD_MORE_COUNT);
    return Number.isFinite(count) && count > 0 ? Math.floor(count) : LOAD_MORE_COUNT;
  }

  /**
   * @param {string} sortValue
   */
  #updateSortControls(sortValue) {
    const activeSort = SORT_VALUES.has(sortValue) ? sortValue : DEFAULT_SORT;
    const status = this.#getSortLabel(activeSort) || this.#getSortLabel(DEFAULT_SORT);

    this.querySelectorAll('[data-sort-status]').forEach((element) => {
      element.textContent = status;
    });

    this.querySelectorAll('input[data-blog-sort-input]').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      input.checked = input.value === activeSort;
    });

    this.querySelectorAll('[role="option"][data-sort-value]').forEach((option) => {
      if (!(option instanceof HTMLElement)) return;

      const isSelected = option.dataset.sortValue === activeSort;
      option.setAttribute('aria-selected', String(isSelected));
      option.tabIndex = isSelected ? 0 : -1;
    });
  }

  /**
   * @param {string} sortValue
   * @returns {string}
   */
  #getSortLabel(sortValue) {
    const option = this.querySelector(`[data-sort-value="${sortValue}"] .sorting-filter__label`);
    return option instanceof HTMLElement ? option.textContent?.trim() ?? '' : '';
  }

  /**
   * @returns {string}
   */
  #getSortValueFromUrl() {
    const sortValue = new URL(window.location.href).searchParams.get('sort_by');
    return sortValue && SORT_VALUES.has(sortValue) ? sortValue : DEFAULT_SORT;
  }

  #closeSortPanels() {
    this.querySelectorAll('[data-blog-sort-details]').forEach((details) => {
      if (details instanceof HTMLDetailsElement) {
        details.open = false;
      }
    });
  }

  #addResponsiveListener() {
    if (!this.#mobileMediaQueryList) return;

    if ('addEventListener' in this.#mobileMediaQueryList) {
      this.#mobileMediaQueryList.addEventListener('change', this.#handleResponsiveBreakpointChange);
      return;
    }

    this.#mobileMediaQueryList.addListener(this.#handleResponsiveBreakpointChange);
  }

  #removeResponsiveListener() {
    if (!this.#mobileMediaQueryList) return;

    if ('removeEventListener' in this.#mobileMediaQueryList) {
      this.#mobileMediaQueryList.removeEventListener('change', this.#handleResponsiveBreakpointChange);
      return;
    }

    this.#mobileMediaQueryList.removeListener(this.#handleResponsiveBreakpointChange);
  }

  #handleResponsiveBreakpointChange = () => {
    const nextInitialVisibleCount = this.#getInitialVisibleCount();

    if (nextInitialVisibleCount === this.#responsiveInitialVisibleCount) {
      return;
    }

    if (this.#isLoading) {
      this.#pendingResponsiveSync = true;
      return;
    }

    void this.#syncResponsiveInitialCount(nextInitialVisibleCount);
  };

  async #syncResponsiveInitialCount(nextInitialVisibleCount = this.#getInitialVisibleCount()) {
    if (this.#isLoading) {
      this.#pendingResponsiveSync = true;
      return;
    }

    if (nextInitialVisibleCount === this.#responsiveInitialVisibleCount) {
      return;
    }

    try {
      this.#setLoadingState(true);

      const pageData = await this.#fetchArticles({
        first: nextInitialVisibleCount,
        filters: this.#activeFilterState,
        sortValue: this.#activeSortValue,
      });

      if (!this.isConnected) return;

      this.#responsiveInitialVisibleCount = nextInitialVisibleCount;
      this.#commitResults(pageData, { append: false });
    } catch (error) {
      console.error(error);
    } finally {
      this.#setLoadingState(false);
    }
  }

  #handlePopState = () => {
    const nextFilters = this.#getFilterStateFromUrl();
    const sortValue = this.#getSortValueFromUrl();

    if (this.#areSameFilters(nextFilters, this.#activeFilterState) && sortValue === this.#activeSortValue) {
      return;
    }

    void this.#applyState(nextFilters, sortValue, { updateUrl: false, historyMode: 'replace' });
  };

  /**
   * @returns {{ value: string, handle: string, label: string, group: string }[]}
   */
  #getInitialFilterState() {
    const urlFilterState = this.#getFilterStateFromUrl();
    if (urlFilterState.length) return urlFilterState;

    return this.#getServerActiveFilterState();
  }

  /**
   * @returns {{ value: string, handle: string, label: string, group: string }[]}
   */
  #getServerActiveFilterState() {
    return this.#dedupeFilterState(
      Array.from(this.querySelectorAll('.blog-posts-active-filters-state [data-active-tag-value]'))
        .filter((element) => element instanceof HTMLElement)
        .map((element) => {
          const value = element.dataset.activeTagValue?.trim() ?? '';
          const handle = element.dataset.activeTagHandle?.trim() ?? '';
          const label = value;

          return { value, handle, label, group: '' };
        })
        .filter((filter) => filter.value && filter.handle)
    );
  }

  /**
   * @returns {{ value: string, handle: string, label: string, group: string }[]}
   */
  #getSelectedConfiguredFilters() {
    return this.#getConfiguredFilters({ checkedOnly: true });
  }

  /**
   * @param {{ checkedOnly?: boolean }} [options]
   * @returns {{ value: string, handle: string, label: string, group: string }[]}
   */
  #getConfiguredFilters({ checkedOnly = false } = {}) {
    return this.#dedupeFilterState(
      Array.from(this.querySelectorAll('.blog-posts-filters-drawer__body .blog-posts-filter-option'))
        .filter((option) => option instanceof HTMLElement)
        .map((option) => {
          const input = option.querySelector('input[name="blog_filter_tags[]"]');
          if (!(input instanceof HTMLInputElement)) return null;
          if (checkedOnly && !input.checked) return null;

          return {
            value: input.value.trim(),
            handle: option.dataset.tagHandle?.trim() ?? '',
            label: option.dataset.filterLabel?.trim() || input.dataset.label?.trim() || input.value.trim(),
            group: option.dataset.filterGroup?.trim() ?? '',
          };
        })
        .filter((filter) => filter?.value && filter.handle)
    );
  }

  /**
   * @returns {{ value: string, handle: string, label: string, group: string }[]}
   */
  #getFilterStateFromUrl() {
    const urlTokens = this.#getFilterTokensFromUrl();

    if (!urlTokens.length) {
      return [];
    }

    const knownFilters = this.#dedupeFilterState([
      ...this.#getConfiguredFilters(),
      ...this.#getServerActiveFilterState(),
    ]);
    const knownByHandle = new Map(knownFilters.map((filter) => [filter.handle, filter]));
    const knownByValue = new Map(knownFilters.map((filter) => [filter.value.toLowerCase(), filter]));

    return this.#dedupeFilterState(
      urlTokens
        .map((token) => {
          const normalizedToken = token.trim();
          if (!normalizedToken) return null;

          const knownFilter = knownByHandle.get(normalizedToken) || knownByValue.get(normalizedToken.toLowerCase());
          if (knownFilter) {
            return {
              value: knownFilter.value,
              handle: knownFilter.handle,
              label: knownFilter.label,
              group: knownFilter.group ?? '',
            };
          }

          return {
            value: normalizedToken,
            handle: handleize(normalizedToken),
            label: normalizedToken,
            group: '',
          };
        })
        .filter(Boolean)
    );
  }

  /**
   * @returns {string[]}
   */
  #getFilterTokensFromUrl() {
    const url = new URL(window.location.href);
    const serializedFilters = url.searchParams.get(FILTER_QUERY_PARAM)?.trim() ?? '';

    if (serializedFilters) {
      return serializedFilters
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean);
    }

    if (!this.#isLegacyTaggedFilterPath(url)) {
      return [];
    }

    const pathSuffix = url.pathname.slice(`${this.#getBlogBasePath()}/tagged/`.length);
    return pathSuffix
      .split('+')
      .map((value) => value.trim())
      .filter(Boolean);
  }

  /**
   * @param {{ value: string, handle: string, label: string, group: string }[]} filters
   * @returns {string}
   */
  #buildStorefrontFilterQuery(filters) {
    const serializedFilters = this.#dedupeFilterState(filters)
      .map((filter) => filter.value?.trim())
      .filter(Boolean)
      .map((value) => `tag:"${escapeSearchQueryValue(value)}"`);

    if (!serializedFilters.length) {
      return '';
    }

    if (serializedFilters.length === 1) {
      return serializedFilters[0];
    }

    return `(${serializedFilters.join(' OR ')})`;
  }

  /**
   * @param {{ value: string, handle: string, label: string, group: string }[]} filters
   * @returns {{ value: string, handle: string, label: string, group: string }[]}
   */
  #dedupeFilterState(filters) {
    const byHandle = new Map();

    for (const filter of filters) {
      if (!filter?.value || !filter.handle) continue;

      const key = `${filter.handle}::${filter.value}`;
      if (!byHandle.has(key)) {
        byHandle.set(key, filter);
      }
    }

    return Array.from(byHandle.values());
  }

  /**
   * @param {{ value: string, handle: string, label: string, group: string }[]} filtersA
   * @param {{ value: string, handle: string, label: string, group: string }[]} filtersB
   * @returns {boolean}
   */
  #areSameFilters(filtersA, filtersB) {
    const serializedA = filtersA.map((filter) => `${filter.handle}::${filter.value}`).sort().join('||');
    const serializedB = filtersB.map((filter) => `${filter.handle}::${filter.value}`).sort().join('||');
    return serializedA === serializedB;
  }

  /**
   * @param {{ value: string, handle: string, label: string, group: string }[]} filters
   * @param {{ sortValue?: string }} [options]
   * @returns {URL}
   */
  #buildFilterUrl(filters, { sortValue = this.#activeSortValue } = {}) {
    const url = new URL(this.blogUrl, window.location.origin);
    const serializedFilters = this.#dedupeFilterState(filters)
      .map((filter) => filter.value.trim())
      .filter(Boolean)
      .join(',');

    if (serializedFilters) {
      url.searchParams.set(FILTER_QUERY_PARAM, serializedFilters);
    } else {
      url.searchParams.delete(FILTER_QUERY_PARAM);
    }

    if (sortValue !== DEFAULT_SORT) {
      url.searchParams.set('sort_by', sortValue);
    } else {
      url.searchParams.delete('sort_by');
    }

    url.searchParams.delete('page');

    return url;
  }

  #normalizeFilterUrl() {
    if (!this.#activeFilterState.length && !this.#isLegacyTaggedFilterPath()) {
      const currentUrl = new URL(window.location.href);

      if (currentUrl.searchParams.has(FILTER_QUERY_PARAM) || this.#isLegacyTaggedFilterPath(currentUrl)) {
        currentUrl.searchParams.delete(FILTER_QUERY_PARAM);
        currentUrl.searchParams.delete('page');
        history.replaceState(history.state, '', currentUrl);
      }

      return;
    }

    const currentUrl = new URL(window.location.href);
    const normalizedUrl = this.#buildFilterUrl(this.#activeFilterState, { sortValue: this.#activeSortValue });

    if (
      currentUrl.pathname === normalizedUrl.pathname &&
      currentUrl.search === normalizedUrl.search
    ) {
      return;
    }

    history.replaceState(history.state, '', normalizedUrl);
  }

  #renderActiveFilters() {
    const container = this.#getActiveFiltersContainer();
    if (!(container instanceof HTMLElement)) return;
    const removeLabel = container.dataset.removeLabel?.trim() ?? '';

    container.replaceChildren();
    container.classList.remove('blog-posts-active-filters--active');

    if (!this.#activeFilterState.length) {
      container.hidden = true;
      return;
    }

    for (const filter of this.#activeFilterState) {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'button-unstyled pills__pill facets-remove__pill';
      chip.dataset.removeTagHandle = filter.handle;
      chip.setAttribute('aria-label', removeLabel ? `${removeLabel} ${filter.label}` : filter.label);

      chip.addEventListener('click', () => {
        if (this.#isLoading) return;

        const filterHandle = filter.handle;
        const nextFilters = this.#activeFilterState.filter((item) => item.handle !== filterHandle);
        void this.#applyState(nextFilters, this.#activeSortValue, { updateUrl: true });
      });

      const label = document.createElement('span');
      label.textContent = filter.label;
      chip.append(label);

      const icon = this.#createActiveFilterRemoveIcon();
      if (icon) {
        chip.append(icon);
      }

      const srText = document.createElement('span');
      srText.className = 'visually-hidden';
      srText.textContent = removeLabel;
      chip.append(srText);

      container.append(chip);
    }

    container.classList.add('blog-posts-active-filters--active');
    container.hidden = false;
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getActiveFiltersContainer() {
    const container = this.querySelector('.blog-posts-active-filters');
    return container instanceof HTMLElement ? container : null;
  }

  #syncFilterInputs() {
    const activeHandles = new Set(this.#activeFilterState.map((filter) => filter.handle));

    for (const input of this.querySelectorAll('.blog-posts-filters-drawer__body input[name="blog_filter_tags[]"]')) {
      if (!(input instanceof HTMLInputElement)) continue;

      const option = input.closest('.blog-posts-filter-option');
      const handle = option instanceof HTMLElement ? option.dataset.tagHandle?.trim() ?? '' : '';

      input.checked = activeHandles.has(handle);
    }
  }

  #renderFilterGroupsFromSources() {
    const container = this.querySelector('.blog-posts-filters-drawer__body');
    if (!(container instanceof HTMLElement)) return;

    const sourceTemplates = Array.from(this.querySelectorAll('template[data-blog-filter-group-source]')).filter(
      (template) => template instanceof HTMLTemplateElement
    );

    if (!sourceTemplates.length) {
      container.replaceChildren();
      return;
    }

    container.replaceChildren(...sourceTemplates.map((template) => template.content.cloneNode(true)));
  }

  #createActiveFilterRemoveIcon() {
    const template = this.querySelector('template[data-blog-active-filter-remove-icon]');
    if (!(template instanceof HTMLTemplateElement)) return null;

    return template.content.firstElementChild?.cloneNode(true) ?? null;
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getEmptyState() {
    const emptyState = this.querySelector('[data-blog-empty-state]');
    return emptyState instanceof HTMLElement ? emptyState : null;
  }

  /**
   * @returns {HTMLTemplateElement | null}
   */
  #getCardTemplate() {
    const template = this.querySelector('template[data-blog-post-card-template]');
    return template instanceof HTMLTemplateElement ? template : null;
  }

  /**
   * @param {URL} [url]
   * @returns {boolean}
   */
  #isLegacyTaggedFilterPath(url = new URL(window.location.href)) {
    const taggedPrefix = `${this.#getBlogBasePath()}/tagged/`;
    return url.pathname === taggedPrefix.slice(0, -1) || url.pathname.startsWith(taggedPrefix);
  }

  /**
   * @returns {string}
   */
  #getBlogBasePath() {
    return new URL(this.blogUrl, window.location.origin).pathname.replace(/\/$/, '');
  }

  async #closeFiltersDrawer() {
    const dialog = this.querySelector('#blog-filters-drawer');

    if (
      dialog instanceof HTMLElement &&
      'closeDialog' in dialog &&
      typeof dialog.closeDialog === 'function'
    ) {
      await dialog.closeDialog();
    }
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  get blogUrl() {
    const url = this.dataset.blogUrl;

    if (!url) throw new Error('The data-blog-url attribute is required');

    return url;
  }

  get blogHandle() {
    const handle = this.dataset.blogHandle;

    if (!handle) throw new Error('The data-blog-handle attribute is required');

    return handle;
  }

  get locale() {
    return this.dataset.locale || document.documentElement.lang || undefined;
  }

  get storefrontEndpoint() {
    const domain = this.dataset.storefrontDomain || DEFAULT_STOREFRONT_DOMAIN;
    const version = this.dataset.storefrontApiVersion || DEFAULT_STOREFRONT_API_VERSION;

    return `https://${domain}/api/${version}/graphql.json`;
  }
}

if (!customElements.get('blog-posts-list')) {
  customElements.define('blog-posts-list', BlogPostsList);
}

/**
 * @param {string} value
 * @returns {string}
 */
function stripHtml(value) {
  const template = document.createElement('template');
  template.innerHTML = value;
  return template.content.textContent?.trim() ?? '';
}

/**
 * @param {string} value
 * @param {number} wordCount
 * @returns {string}
 */
function truncateWords(value, wordCount) {
  const words = value.split(/\s+/).filter(Boolean);
  if (words.length <= wordCount) return words.join(' ');
  return `${words.slice(0, wordCount).join(' ')}...`;
}

/**
 * @param {string} value
 * @returns {string}
 */
function handleize(value) {
  return String(value)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * @param {string} value
 * @returns {string}
 */
function escapeSearchQueryValue(value) {
  return String(value)
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"');
}

/**
 * @param {string} isoDate
 * @param {string | undefined} locale
 * @returns {string}
 */
function formatDate(isoDate, locale) {
  if (!isoDate) return '';

  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
}
